/**
 * iOS push-notification registration.
 *
 * On native (Capacitor iOS), we ask iOS for an APNs token, then POST it
 * to the Assistentus backend so server-side jobs (morning brief, weekly
 * summary, background task completion) can deliver pushes even when the
 * app is in the background or fully closed.
 *
 * On web (browser), this whole file is a no-op — web push goes through a
 * different stack and we'll wire that separately if/when needed.
 */

import { Capacitor } from '@capacitor/core';
import config from './config';

let _registered = false;
let _currentToken: string | null = null;

async function postToken(token: string, supabaseUserId: string): Promise<boolean> {
  try {
    const res = await fetch(`${config.ASSISTENTUS_API_URL}/api/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        supabase_user_id: supabaseUserId,
        device_token: token,
        platform: 'ios',
        bundle_id: 'pro.gitech.assistentus',
        app_version: (window as any).__APP_VERSION__ || undefined,
        device_name: navigator.userAgent.slice(0, 80),
        locale: navigator.language || 'ru-RU',
      }),
    });
    return res.ok;
  } catch (e) {
    console.warn('[Push] POST /push/register failed:', e);
    return false;
  }
}

export async function ensurePushRegistered(supabaseUserId: string | undefined | null): Promise<void> {
  if (!supabaseUserId) return;
  if (!Capacitor.isNativePlatform()) return;
  if (Capacitor.getPlatform() !== 'ios') return;
  if (_registered && _currentToken) {
    // Already done this session — re-POST opportunistically so last_seen_at
    // stays current, but don't re-register listeners.
    postToken(_currentToken, supabaseUserId).catch(() => {});
    return;
  }

  let PushNotifications: any;
  try {
    const mod = await import('@capacitor/push-notifications');
    PushNotifications = mod.PushNotifications;
  } catch (e) {
    console.warn('[Push] @capacitor/push-notifications not available:', e);
    return;
  }

  try {
    const permStatus = await PushNotifications.checkPermissions();
    let granted = permStatus.receive === 'granted';
    if (!granted && permStatus.receive === 'prompt') {
      const req = await PushNotifications.requestPermissions();
      granted = req.receive === 'granted';
    }
    if (!granted) {
      console.info('[Push] Permission denied — skipping APNs registration');
      return;
    }

    await PushNotifications.addListener('registration', async (token: { value: string }) => {
      _currentToken = token.value;
      const ok = await postToken(token.value, supabaseUserId);
      console.info(`[Push] APNs token sent to backend: ok=${ok}`);
    });

    await PushNotifications.addListener('registrationError', (err: unknown) => {
      console.warn('[Push] APNs registration error:', err);
    });

    // Fired when a push lands while the app is in the foreground. The
    // OS does NOT show a banner in that case, so emit a window event the
    // chat / notes UI can listen to (toast, badge, etc).
    await PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
      try {
        window.dispatchEvent(new CustomEvent('assistentus-push-received', { detail: notification }));
      } catch { /* noop */ }
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
      try {
        window.dispatchEvent(new CustomEvent('assistentus-push-action', { detail: action }));
      } catch { /* noop */ }
    });

    await PushNotifications.register();
    _registered = true;
  } catch (e) {
    console.warn('[Push] Setup failed:', e);
  }
}
